/**
 * `@tento/lona/sheets` — standalone row data-model library.
 *
 * Package overview, stores and shims: `sheets/.docs/content/rows-intro.md`.
 *
 * This barrel is the DATA MODEL: identity, rows, cells, the sheet, the
 * invalidation vocabulary, the in-memory backend, facades and indices — every
 * name listed explicitly (no `export *`). What only a render front-end or a
 * transport implements against — the concrete row variants beyond the role
 * bases, the transport backends, the shape / encoding / facade registries and
 * encoding decoders, the layout engine, render plan, scene and scene diff, the
 * tree pipeline, the TUI reference, the per-type plugin specs and binding
 * registry, the render-skip ledger — lives on `@tento/lona/sheets/platform`
 * alone. A name is exported from one of the two, never both.
 *
 * @module
 */

// The one "maybe" in every published signature: `T | null`, a module type (a
// consumer needs no ambient declaration). See `./core/option`.
export type { Option } from "./core/option.ts";

// Branded ids (`EventId.parse`, `TaskId.of`, …): an id of one kind is not a
// plain string. See `./ids/ids`.
export { AuthId, CalendarId, CellId, EventId, RowUuid, SheetUuid, SlotId, SyncKey, TaskId, TemplateRowId, UserId } from "./ids/ids.ts";
// A sheet's id, parsed and interned (`SheetId.of`).
export { SheetId } from "./ids/sheet-id.ts";
// A sheet named by its id or its lookup key (`SheetKey.toRef()`).
export { SheetKeyRef } from "./ids/sheet-key-ref.ts";

// Wire-protocol namespace — the generated types for the rows package's HTTP
// boundary. A strict subset of `@tento/lona`'s SDK-wide `Network`.
export { SheetsNetwork } from "./wire/rows-network.ts";
export { Patch, RowPatch } from "./rows/row-patch.ts";

// Data shape vocabulary
export type {
  SourceShape,
  CellStyle,
  CellValue,
  Dtype,
  Slot,
  Weather,
} from "./core/dtype.ts";
// `TaskCellValue` and `CalendarEvent` are both an interface and a namespace
// (`TaskCellValue.of`, `TaskCellValue.Status`, `CalendarEvent.of`):
// re-exported as values.
export { CalendarEvent, TaskCellValue } from "./core/dtype.ts";
// `CellEntry` is both an interface and a namespace (decoder helpers
// `CellEntry.SingleCard`, `CellEntry.MultiCard`, `CellEntry.Timepoint`).
// Re-exported as a value so consumers can call the decoders.
export { CellEntry } from "./core/dtype.ts";

// Cell-lane placement resolver — the single authority mapping the orthogonal
// (cardinality × temporal-kind) axes onto a storage lane. Shared by the
// lona-js `RowData` and the sheets `RowCellData`; the Stage-2 lane-rows seam.

// Layout / renderer identifiers
export { LayoutKind } from "./rows/slot.ts";
export type { RendererId } from "./rows/slot.ts";

// Layout base types — narrower shapes than yuzu-ui's
// CellLayout / ColumnIndex.Key.Resolved.Default / SheetLayoutSpec,
// ported for the pure LayoutEngine + RenderPlan math.
export type {
  CellLayout,
  CellLayoutOptions,
  LayoutSpec,
  RowLayoutShape,
  SheetLayoutSpec,
} from "./render/layout-types.ts";

// Pure timescale math (dates ↔ columns). The render-mode classifier built on
// it (`RowRenderMode`), the layout engine, render plan, scene + diff, TUI
// reference, geometry snapshot and plugin catalog are renderer-implementor
// names: `@tento/lona/sheets/platform`.
export { Timescale } from "./render/timescale.ts";

// Generated row-type catalog (mirrors tento-lona-connector::row_type). Exposed
// namespaced so the canonical `Layout`/`Inert`/`Alias`/`Source` const groups
// don't collide with other package exports (e.g. `Layout` from ./rows/slot).
/**
 * The catalog of row wire types, generated from the server's row-type table.
 */
export * as RowTypeCatalog from "./@generated/row-type-catalog.ts";

// The per-type plugin manifest, plugin data providers and render-plugin
// binding registry are `@tento/lona/sheets/platform`.

// Descriptor + role
export { type Descriptor, Role } from "./rows/descriptor.ts";

// Identity
export { RowId } from "./ids/row-id.ts";
// The `event.v1` wire codec: a server event row → the decoded `Event`.
export { eventDbToEvent } from "./encodings/event-v1.ts";
export { RowKey } from "./ids/row-key.ts";
export { RowLocalId } from "./ids/row-local-id.ts";
export { EventOrigin } from "./tx/event-origin.ts";
export { RowNetworkKey } from "./ids/row-network-key.ts";
export { RowTypeName } from "./ids/row-type-name.ts";
export { SheetNetworkKey } from "./ids/sheet-network-key.ts";

// The row spec a sheet adds (`AddRowSpec`). The full `Sheet` is not on this
// barrel, and no value here hands one out (`tools/lint/read-faces`): an
// application holds a sheet only as its faces (`SheetModel`,
// `SheetModelReads`; `RawSheet.hydrate` answers the reads face); the type and
// its builder (`hydrateSheet`) are an implementor's (`@tento/lona/sheets/platform`).
export type { AddRowSpec } from "./sheet/sheet.ts";
// The sheet's two handed-out faces: a row's `sheetReads` (`SheetModelReads`)
// and the app's `SheetHandle.model` (`SheetModel`: reads + the persisting verbs);
// every row either hands out is a `ModelRowReads` (the row's reads, no writer).
export type { SheetModel, SheetModelReads } from "./sheet/rows-sheet-view.ts";
export type { ModelRowReads } from "./rows/model-row-reads.ts";
// The per-client lookup tables a sheet resolves its rows through
// (`ClientConfig.registries`, `Sheet.registries`).
export { SheetRegistries } from "./sheet/sheet-registries.ts";
// The render-pipeline flags a client's registries carry (`SheetRegistries.render`).
export { RenderFlags } from "./render/render-flags.ts";
// `ModelRow` is a type here, the class on no entry: an app narrows a row with
// `RowKind.of(row)` (below); an implementor dispatches on the variant classes
// (`@tento/lona/sheets/platform`), which extend it.
export type { Capabilities, ModelRow } from "./rows/model-row.ts";

// A host's switchable `RenderFlags` (`RenderFlagControls`) is declared on
// `@tento/lona/host`.

// The render-skip ledger (`RenderSkip`, `RenderCursors` / `RenderCursor` /
// `RenderSurfaceLease`) a paint surface borrows is `@tento/lona/sheets/platform`.

// What every durable (debounced) write returns — shared by both packages,
// imported from here only (`@tento/lona` does not re-export it).
export { Durable } from "./core/durable.ts";

// Invalidation — canonical event vocabulary + sink
export {
  Actor,
  CalendarPreferencesChanged,
  CalendarVisibilityChanged,
  ColumnDataChanged,
  EventTimezoneChanged,
  Invalidation,
  InvalidationSink,
  LayoutChanged,
  NewRowInserted,
  RowDataChanged,
  RowRangeDataChanged,
  RowHeightChanged,
  RowLegendChanged,
  RowsReordered,
  RowSyncStateChanged,
  SchedulingLinksChanged,
  SheetDeleted,
  SheetListChanged,
  SheetMetadataChanged,
  SheetStructureChanged,
  SheetWidthChanged,
  TasksChanged,
  ThemeChanged,
  TimepointsChanged,
  TimezoneChanged,
  ViewportsInvalidated,
  WindowResized,
} from "./tx/invalidation.ts";

// Sync state — per-row background-fetch status
export { SyncPollThrottledError, RowSyncState } from "./sync/sync-state.ts";
export type {
  SyncKeyStatus,
  SyncPoll,
  SyncStatus,
  SyncStatusResponse,
} from "./sync/sync-state.ts";

// Stores are internal: `Sheet.stores` is private and `SheetStores` is not on
// this barrel. Consumers read and write through the named `Sheet` methods
// (`rowMeta`, `parentOf`, `childrenOf`, `rootIds`, `peekCells`, `peekCell`,
// `renderCursors`, `sceneStamp`, `seedCells`, `patchAttributesLocal`); the
// package's own modules use the module-private friend `sheetStores`, which
// this repository's tests reach through the workspace-only
// `@tento-lona/sheets/internal-testing`. The store-derived types are
// `AliasInstance` (alias-templates section) and `RowMetadata`, which is on
// `@tento/lona/sheets/platform`.

// Variants — a row is narrowed with `RowKind.of(row)`: its `RowKind`
// names the variant (`kind`) and the role base (`role`) and carries the row's
// reads face typed as that variant's reads (`SourceRowReads`,
// `AliasRowReads`, …; `RowKind.RowOf<"alias">` names the type in an
// annotation) — never the live row, so no writer is reachable from it. It
// answers a sheet reads' row face as well as a live row; `instanceof` does
// neither. The classes themselves — the role bases, the
// named variants and every other concrete subclass, for a renderer
// implementor's dispatch — the default-renderer dispatch and `parseRow` are
// renderer-implementor names: `@tento/lona/sheets/platform`.
export { RowKind } from "./rows/row-kind.ts";
export type {
  AliasRowReads,
  CalendarAliasRowReads,
  ColumnNumberRowReads,
  FormulaDataSourceReads,
  LayoutRowReads,
  RendererRowReads,
  SourceRowReads,
  ColumnTimezoneRowReads,
  ColumnWeatherRowReads,
} from "./rows/row-kind.ts";
// The calendar body's timed-grid config (a config shape and its readers, not a row).
export { TimedGridConfig } from "./rows/variants/index.ts";

// Backend interface
export { BatchFetchThrottledError, Etag } from "./backends/rows-backend.ts";
export type {
  Backend,
  BatchRowCells,
  CellChange,
  FetchedCells,
  RowChange,
  Unsubscribe,
} from "./backends/rows-backend.ts";

// The one checked-request error base, shared with `@tento/lona` (which
// re-exports it): `RowsHttpBackendRequestError` and every `@tento/lona` boundary
// error extend it, so `e instanceof SdkRequestError` covers them all.
export { RowWriteError, SdkRequestError } from "./core/errors.ts";
export type {
  SdkRequestBoundary,
  SdkRequestFailure,
  SdkRequestMethod,
  SdkRequestOutcome,
  SdkRequestReason,
} from "./core/errors.ts";
// How a generated wire type decodes: `Network.*.parse` throws a
// `WireDecodeError`; a generated `of`, like every create verb, takes the type
// itself (every key, `null` stated).
export { WireDecode, WireDecodeError } from "./wire/wire-decode.ts";

// The in-memory backend: it serves production offline paths (the app's
// template previews, the CLI's offline sheet) as well as fixtures. The
// transport and normalizing backends (`RowsHttpBackend`, `CanonicalBackend`,
// `NormalizingBackend`) are `@tento/lona/sheets/platform`.
export { MemoryRowsBackend } from "./backends/memory-backend.ts";
export { RowProvider } from "./rows/row-provider.ts";

// Shape-driven dataSource interop
export { ShapeKey, defineShape } from "./core/shape.ts";
export type { Shape } from "./core/shape.ts";
export { FacadeKey } from "./facades/facade.ts";
// The shape / encoding / facade registries and the built-in encoding decoders
// (wire → canonical) are `@tento/lona/sheets/platform`.

// Facades — typed row APIs obtained via `row.as(FACADE_KEY)`
export {
  DATA_FACADE,
  DataFacade,
  SchedulingLinkAttributes,
} from "./facades/index.ts";

// Indices — underlying caches behind index-backed facades
export {
  DataIndex,
  EventItemAllDay,
  EventItemTimed,
  TaskCalendarItem,
  TasksIndex,
} from "./indices/index.ts";

// Mutation substrate — the domain seam of the `base ⊕ overlay` optimistic
// write path (`Reduce`, the op a domain binding applies to the snapshot) is
// `@tento/lona/sheets/mutation`.

// Regression math — Nadaraya-Watson kernel smoother + imputation
// helper. Consumed by `RegressionIndex`; exposed so headless
// clients and tests can evaluate curves directly.
export {
  impute,
  Kernel,
  kernelSmooth,
} from "./regression/kernel-smooth.ts";
export type { KernelSmoothResult } from "./regression/kernel-smooth.ts";
export type {
  CellAdapter,
  TimepointCellEntry,
  Graph,
  GraphSpecNode,
} from "./indices/index.ts";

// Raw-sheet shape + declarative hydration helpers
// `EventItem` is one name for the type and its factories (`make`, `fromEvents`).
export { EventItem } from "./indices/events/event-item.ts";
// A change to an event: set / clear per field, one wire encoder, the local
// paint derived from it.
export { EventPatch } from "./indices/events/event-patch.ts";
export { RawRow, RawSheet } from "./raw/raw-sheet.ts";
// The frame-budget port `RawSheet.HydrateDeps.recomputeScheduler` and
// `ClientConfig.recomputeScheduler` take.
export type { RecomputeScheduler } from "./sheet/sheet-recompute-queue.ts";
export type { RawCell } from "./raw/raw-sheet.ts";

// Per-wire-type static metadata registry + attribute reconciler
// The built-in row-type registry itself stays package-internal: read a type's
// spec through a sheet's `registries.rowTypes.get(wireType)` (`SheetRegistries`).
export type {
  ChildSpec,
  RawRowInput,
  RowTypeSpec,
} from "./rows/row-type-spec.ts";
export { BuiltinAliasFactory } from "./aliases/builtin-alias-factories.ts";
export type {
  AliasProps,
  AliasTemplate,
  DynamicTail,
  PropSchema,
} from "./aliases/alias-templates.ts";
export type { AliasInstance } from "./stores/alias-instance-store.ts";

// Tree flatten + subsumption (`flatten`, `TreeFlattener`, …) is
// `@tento/lona/sheets/platform`.
export { PropertyListProjection } from "./render/tree-projection.ts";
export { ModelRowOptions } from "./rows/row-options.ts";
export { RowEnvelopes } from "./rows/row-envelopes.ts";
export { ProjectionContext } from "./render/projection-context.ts";

// Ephemeral-sheet URL serialization (flat-indexed + JSON formats).
export { EphemeralSheetSerde } from "./raw/ephemeral-sheet-serde.ts";

// The calendar / task-list config providers a rows-side wiring
// (`ClientConfig.rowsOptions`) supplies. `SheetModelCache` (the client-scoped
// "open a sheet" facade), `IngestionGate` and `SyncKeyWatcher` are engine
// names: `@tento/lona/sheets/platform`.
export type {
  CalendarConfigCalendar,
  CalendarConfigProvider,
} from "./providers/calendar-config-provider.ts";
export type { TaskListConfigProvider } from "./providers/task-list-config-provider.ts";

// Reserved-sheet catalog — client-owned table of ephemeral sheet
// templates (`:~lona:home`, `:calendar`, `:garmin`, …). The accessor
// consults it when the server returns null for a sheet key.
export { BUILTIN_SHEET_CATALOG, isBuiltinSheetKey } from "./raw/ephemeral-sheet-catalog.ts";

// Sheet definitions — the unified model behind builtins and templates
// (a builtin is a definition with a reserved `lookupKey`) + its single
// hydration path. The builtin catalog delegates to `hydrateSheetDefinition`;
// the app's raw-sheet templates can adopt it next.
export { hydrateSheetDefinition } from "./sheet/sheet-definition.ts";
export type { SheetDefinition, SheetDefinitionRow } from "./sheet/sheet-definition.ts";

// System rows — canonical header / footer / debug / ephemeral
// label-banner definitions + builder. Pure function of
// `SystemRowsConfig` per §8.8; lona-js's wrapper reads preferences
// and passes the resolved list in.
export { FOOTERS_STATIC, HEADERS_STATIC, SystemRow, SystemRows } from "./rows/system-rows.ts";
export type {
  HeaderSecondRowKind,
  SystemRowDef,
  SystemRowsConfig,
} from "./rows/system-rows.ts";

// Sheet view model — the core per-sheet render coordinator
// (tree + viewConfig + anchorDate + timescale). lona-js's own
// `SheetViewModel` wraps this and adds SDK / renderer / system-
// rows concerns.
export { SheetViewConfig } from "./sheet/sheet-view-config.ts";
export type { RendererTz } from "./sheet/sheet-view-config.ts";

// Chart — pure data-side primitives. `ChartViewModel` (the chart's view
// model: size, pointer, graph data; no DOM) lives in `@tento-core`
// (`@tento-core/chart/chart-view-model`), is published on `@tento/lona/platform`,
// and consumes these.
export { AxisRange, formatValue, LinearYScaler } from "@tento-core/chart/chart-types";

// The declarative op runner (`applyOp`, `isAssertOp`, `rowByLabel`, the
// `FixtureOp` / `Op` types) writes a sheet's stores around the checked verbs:
// this repository's fixtures reach it through the workspace-only
// `@tento-lona/sheets/internal-testing`; no published entry carries it.

// Formula evaluation — Lisp environment + dependency-driven engine
export {
  evaluateFormula,
  ModelLispEnvironment,
  type FormulaSheetReads,
} from "./formula/model-formula.ts";

// The short-format tree parser + serializer (TREE-SPEC.md: `ParsedTree`,
// `ParsedRendered`, `RenderedTree`, `TreeParseError`, `TreeColumn`,
// `TreeMetadata`) is a render-front-end tool: it lives on
// `@tento/lona/sheets/platform` for CLI / TUI implementors and fixtures.
// The tree builders (`HydratedTree`, `SceneTree`), the reactive
// `TreeWatcher` and its `TreeDiff` vocabulary, and `StructuralCoHide` are
// `@tento/lona/sheets/platform`.

// ─── Canonical names (SDK cleanup) ────────────────────────────────────
// The model-side names (`SheetsNetwork`, `ModelRow`, `ModelRowOptions`,
// `ModelLispEnvironment`) are exported where their modules are, above. The
// qualifier (`Model*`, or `Sheets*` for the wire namespace) marks a name
// whose stem `@tento/lona` or `@tento/lona/sheets-ui` also uses; they carry
// no unqualified alias.

// The SDK JSON types (cell data, row attributes, row metadata); see ./core/json.
export { Json } from "./core/json.ts";
export type { JsonObject } from "./core/json.ts";

// The types the published signatures name (tools/lint/published-type-refs).
export type { AliasLocalState, AliasTemplateContext, DataRef, StaticRule } from "./aliases/alias-templates.ts";
export type { SurfaceKind } from "./backends/rows-backend.ts";
export type { AliasDescriptor, InertDescriptor, LayoutDescriptor, RendererDescriptor } from "./rows/descriptor.ts";
export type { ModelFormulaContext } from "./formula/model-formula.ts";
export type { DataIndexDiagnostics } from "./indices/data-index.ts";
export type { TasksIndexDiagnostics } from "./indices/tasks-index.ts";
export type { StackingInfo } from "./render/layout-types.ts";
export type { KernelFn } from "./regression/kernel-smooth.ts";
export type { RowMeta } from "./rows/row-type-spec.ts";
export type { SheetModelReadMembers } from "./sheet/rows-sheet-view.ts";
export type { Probed } from "./core/shape.ts";
export type { HydrateOptions } from "./sheet/sheet-definition.ts";
export type { SystemRowInput, SystemRowsResult, TreeRowLike } from "./rows/system-rows.ts";
export type { TaskListConfigItem } from "./providers/task-list-config-provider.ts";
export type { ComputedSourceBuilder } from "./rows/variants/computed-source-registry.ts";
